import { memo, useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { typography, transitions } from "../tokens";

interface BarcodeScannerProps {
  onScan: (code: string) => void;
  onClose: () => void;
  title?: string;
}

export const BarcodeScanner = memo(function BarcodeScanner({ onScan, onClose, title }: BarcodeScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    const scannerId = "barcode-scanner-container";
    let scanner: Html5Qrcode | null = null;
    let stopped = false;

    const startScanner = async () => {
      scanner = new Html5Qrcode(scannerId);
      scannerRef.current = scanner;

      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (!stopped) {
              stopped = true;
              onScan(decodedText);
              scanner?.stop().catch(() => {});
            }
          },
          () => {}
        );
        if (!stopped) setStarting(false);
      } catch (err: any) {
        if (!stopped) {
          setStarting(false);
          setError(err?.message ?? "Camera not available");
        }
      }
    };

    startScanner();

    return () => {
      stopped = true;
      if (scanner?.isScanning) {
        scanner.stop().catch(() => {});
      }
      scanner?.clear();
    };
  }, [onScan]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "rgba(0,0,0,0.8)" }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#fff" }}>
          {title ?? "Scan code"}
        </span>
        <button
          onClick={() => { if (scannerRef.current?.isScanning) scannerRef.current.stop().catch(() => {}); onClose(); }}
          style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "4px", display: "flex" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>

      {/* Scanner area */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
        <div id="barcode-scanner-container" ref={containerRef} style={{ width: "100%", maxWidth: "400px" }} />
        {starting && (
          <div style={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
            <span style={{ width: "24px", height: "24px", border: "3px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#aaa" }}>Starting camera...</span>
          </div>
        )}
        {error && (
          <div style={{ position: "absolute", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", padding: "24px", textAlign: "center" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#f87171" }}>{error}</span>
            <button
              onClick={() => { if (scannerRef.current?.isScanning) scannerRef.current.stop().catch(() => {}); onClose(); }}
              style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, color: "#fff", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", padding: "10px 20px", cursor: "pointer" }}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div style={{ padding: "16px 20px", background: "rgba(0,0,0,0.8)", textAlign: "center" }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888" }}>
          Point the camera at a barcode or QR code
        </span>
      </div>
    </div>
  );
});

/** Returns true if the device likely has a camera (mobile/tablet/iPad) */
export function hasMobileCamera(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  // iPadOS 13+ reports as Mac, detect via touch support
  const isIPad = /macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return isIPad || /android|iphone|ipad|ipod|mobile|tablet/.test(ua);
}
